/**
 * Tiers concerné par un litige — source unique.
 *
 * Selon la typologie, le tiers mis en cause vit dans l'un de trois champs :
 * `client_id`, `supplier_id` ou `carrier_id`. L'enrichissement du backend
 * résout les trois en `client_name`, `supplier_name` et `carrier_name`.
 *
 * Trois écrans reconstruisaient ce choix à la main, et deux d'entre eux
 * OUBLIAIENT `carrier_name` :
 *
 *     c.client_name || c.supplier_name || "Interne"
 *
 * Un litige de type Transport s'affichait donc « Interne » — dans la liste,
 * dans le tableau de bord et dans l'export CSV — c'est-à-dire précisément
 * l'inverse de ce qu'il est : un litige imputé à un prestataire externe.
 *
 * D'où ce module. Le jour où un quatrième champ de rattachement apparaîtra,
 * il n'y aura qu'un endroit à modifier.
 */

/** Libellé de la nature du tiers, pour les écrans qui l'affichent. */
export const NATURE_CONCERNE = {
  client: "Client",
  fournisseur: "Fournisseur",
  transporteur: "Transporteur",
};

/** Valeur affichée lorsqu'aucun tiers n'est en cause — litige interne. */
export const AUCUN_TIERS = "Interne";

/**
 * Nom du tiers concerné, ou « Interne » si le litige n'en met aucun en cause.
 * @param {object} litige fiche enrichie par le backend
 */
export function nomConcerne(litige) {
  if (!litige) return AUCUN_TIERS;
  return (
    litige.client_name ||
    litige.supplier_name ||
    litige.carrier_name ||
    AUCUN_TIERS
  );
}

/** Nature du tiers concerné (`"client"` | `"fournisseur"` | `"transporteur"`), ou `null`. */
export function natureConcerne(litige) {
  if (!litige) return null;
  if (litige.client_name) return "client";
  if (litige.supplier_name) return "fournisseur";
  if (litige.carrier_name) return "transporteur";
  return null;
}

/** Identifiant du logo du tiers concerné, ou `null`. */
export function logoConcerne(litige) {
  if (!litige) return null;
  return (
    litige.client_logo_file_id ||
    litige.supplier_logo_file_id ||
    litige.carrier_logo_file_id ||
    null
  );
}

/** Identifiant du tiers concerné, quelle que soit sa nature. Utile aux filtres. */
export function idConcerne(litige) {
  if (!litige) return null;
  return litige.client_id || litige.supplier_id || litige.carrier_id || null;
}
